import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {forwardRef, useCallback, useEffect, useRef, useState} from 'react'
import {UserSmall} from '@/features/users/types'
import {InputSelectUserTrigger} from '@/features/users/components/input-select-user/InputSelectUserTrigger'
import {InputSelectUserListType} from '@/features/users/components/input-select-user/InputSelectUsersListType'
import {TeamGroup} from '@/features/treatment-requests/teams/types'
import {InputSelectTeamMembersList} from '@/features/users/components/input-select-user/InputSelectTeamMembersList'
import {InputSelectUsersList} from '@/features/users/components/input-select-user/InputSelectUsersList'
import {SelectComboboxInput, SelectComboboxPopover} from '@/components/ui/select-atoms/SelectAtoms'
import {useInputSearch} from '@/hooks/useInputSearch'
import {useGetInfiniteUsers} from '@/features/users/queries/useGetInfiniteUsers'
import {useGetInfiniteTeams} from '@/features/treatment-requests/teams/queries/useGetInfiniteTeams'
import {TableFilterKey} from '@/types'

export type UserSmallSelection = Pick<UserSmall, 'id' | 'fullName'> & {
    specializationName?: UserSmall['specializationName']
    avatarUrl?: UserSmall['avatarUrl']
    email?: UserSmall['email']
    group?: TeamGroup
    specializationId?: string
}

export interface InputSelectUserProps {
    disabled?: boolean
    label?: string
    value: UserSmallSelection | null
    onChange: (value: UserSmallSelection | null) => void
    placeholder?: string
    errorMessage?: string
    onClear?: () => void
    disabledOptions?: string[]
    treatmentRequestId?: string
    projectId?: string
    filters?: Partial<Record<TableFilterKey, unknown>>
    isLoading?: boolean
    displayValue?: (userId?: string) => React.ReactNode
    sameWidth?: boolean
}

export const InputSelectUser = forwardRef<HTMLSelectElement, InputSelectUserProps>(
    (
        {
            disabled,
            label,
            onChange,
            errorMessage,
            placeholder,
            onClear,
            value,
            disabledOptions,
            treatmentRequestId,
            projectId,
            filters,
            isLoading,
            displayValue,
            sameWidth
        },
        ref
    ) => {
        const {t} = useTranslation()
        const [isQueryEnabled, setIsQueryEnabled] = useState(false)
        const [listType, setListType] = useState<'team' | 'all'>(treatmentRequestId ? 'team' : 'all')
        const inputUserRef = useRef(null)

        const {searchValue, onSearch, onResetSearch} = useInputSearch()
        const {remappedData} = useGetInfiniteUsers(
            {
                limit: 20,
                search: searchValue,
                filters
            },
            {enabled: !disabled && isQueryEnabled && listType == 'all'}
        )

        const {data: teamMembers} = useGetInfiniteTeams({
            urlParams: {treatmentRequestId: treatmentRequestId ?? ''},
            params: {responseType: 'small', limit: 20, page: 1, search: searchValue, projectId: projectId},
            options: {enabled: !!treatmentRequestId && listType == 'team' && isQueryEnabled}
        })

        // Handle selection change
        const handleValueChange = useCallback(
            (selectedId: string) => {
                if (listType == 'all') {
                    onChange(remappedData.find(user => user && user.id === selectedId) ?? null)
                } else {
                    // Find the team member and return user data in expected format
                    const teamMember = teamMembers?.pages
                        .flatMap(page => page.data)
                        .find(member => member.user.id === selectedId)
                    if (teamMember) {
                        onChange({
                            id: teamMember.user.id,
                            fullName: teamMember.user.fullName,
                            group: teamMember.group,
                            specializationName: teamMember.user.specializationName,
                            specializationId: teamMember.user.specializationId
                        })
                    } else {
                        onChange(null)
                    }
                }
            },
            [listType, remappedData, teamMembers, onChange]
        )

        // Set list type to team if treatment request id is provided
        useEffect(() => {
            if (treatmentRequestId) {
                setListType('team')
            } else {
                setListType('all')
            }
        }, [treatmentRequestId])

        const renderUsersList = useCallback(
            (listType: 'team' | 'all') => {
                if (listType == 'team' && treatmentRequestId) {
                    return (
                        <InputSelectTeamMembersList
                            treatmentRequestId={treatmentRequestId}
                            disabled={disabled ?? false}
                            isQueryEnabled={isQueryEnabled}
                            disabledOptions={disabledOptions ?? []}
                            search={searchValue}
                            projectId={projectId}
                        />
                    )
                }
                return (
                    <InputSelectUsersList
                        disabled={disabled ?? false}
                        isQueryEnabled={isQueryEnabled}
                        disabledOptions={disabledOptions ?? []}
                        search={searchValue}
                        filters={filters}
                    />
                )
            },
            [disabled, isQueryEnabled, disabledOptions, searchValue, filters, projectId]
        )

        return (
            <ComboboxProvider setValue={onSearch} resetValueOnHide>
                <SelectProvider
                    placement="bottom-end"
                    value={value?.id ?? ''}
                    setValue={handleValueChange}
                    setOpen={isOpen => {
                        onResetSearch(inputUserRef)
                        setIsQueryEnabled(isOpen)
                    }}
                >
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        {label && <Label>{label}</Label>}
                        <InputSelectUserTrigger
                            ref={ref}
                            disabled={disabled || isLoading}
                            value={value}
                            placeholder={placeholder}
                            errorMessage={errorMessage}
                            onClear={onClear}
                            isLoading={isLoading}
                            displayValue={displayValue}
                        />

                        {errorMessage && (
                            <Flexbox align="center" gap={1.5}>
                                <InputStatusIcon hasError={!!errorMessage} />
                                <InputHelpText error={errorMessage} />
                            </Flexbox>
                        )}

                        <SelectComboboxPopover sameWidth={sameWidth}>
                            {treatmentRequestId && (
                                <InputSelectUserListType listType={listType} setListType={setListType} />
                            )}
                            <SelectComboboxInput ref={inputUserRef} placeholder={t('commons:search_placeholder')} />
                            {renderUsersList(listType)}
                        </SelectComboboxPopover>
                    </Flexbox>
                </SelectProvider>
            </ComboboxProvider>
        )
    }
)
